'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Plus,
  Undo,
  Redo,
  Sparkles,
  Moon,
  Sun,
  Laptop,
  Thermometer,
  Clock,
  CheckCheck,
  Bell,
  Paperclip,
  MapPin,
  User,
  Smile,
  MessageSquare,
  Calendar,
  SearchX,
} from 'lucide-react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command'
import { Kbd, KbdGroup } from '@/components/ui/kbd'
import { useTheme } from '@/components/theme-context'
import { actions } from '@/store'
import { useCommandCenterActions } from '@/components/tasks/hooks/use-command-center-actions'
const isEditableTarget = (target) => {
  if (!(target instanceof HTMLElement)) return false
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable
}
const isCommandCenterShortcut = (event) => {
  const key = event.key.toLowerCase()
  const hasModifier = event.metaKey || event.ctrlKey || event.altKey
  const hasShiftCommand = event.shiftKey && (event.metaKey || event.altKey)
  return (
    (key === 'k' && hasModifier) ||
    ((key === 'k' || key === 'p') && hasShiftCommand) ||
    (event.code === 'Space' && (event.ctrlKey || event.altKey))
  )
}
const isPlainSpacePress = (event) =>
  event.code === 'Space' && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey
export function CommandCenter({
  onCreateTask,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onToggleWeatherUnit,
  currentWeatherUnit,
  onToggleTimeFormat,
  timeFormat,
  onToggleTimeVisibility,
  onToggleWeatherVisibility,
}) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [feedback, setFeedback] = useState('')
  const { setTheme } = useTheme()
  const { isRunning, runPrompt } = useCommandCenterActions()
  useEffect(() => {
    const down = (e) => {
      // Command Center shortcuts: Cmd/Ctrl+K, Opt+K, Cmd/Opt+Shift+K/P, Ctrl/Opt+Space
      if (isCommandCenterShortcut(e)) {
        e.preventDefault()
        setOpen((prev) => !prev)
        return
      }
      if (!isPlainSpacePress(e)) return
      if (!isEditableTarget(e.target)) {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])
  const runCommand = useCallback((command) => {
    setOpen(false)
    setInputValue('')
    setFeedback('')
    command()
  }, [])
  const features = useMemo(
    () => [
      {
        key: 'subtasks',
        label: 'Subtasks',
        icon: <CheckCheck className="mr-2 h-4 w-4" />,
      },
      {
        key: 'reminders',
        label: 'Reminders',
        icon: <Bell className="mr-2 h-4 w-4" />,
      },
      {
        key: 'attachments',
        label: 'Attachments',
        icon: <Paperclip className="mr-2 h-4 w-4" />,
      },
      {
        key: 'location',
        label: 'Location',
        icon: <MapPin className="mr-2 h-4 w-4" />,
      },
      {
        key: 'assignee',
        label: 'Assignee',
        icon: <User className="mr-2 h-4 w-4" />,
      },
      {
        key: 'reactions',
        label: 'Reactions',
        icon: <Smile className="mr-2 h-4 w-4" />,
      },
      {
        key: 'comments',
        label: 'Comments',
        icon: <MessageSquare className="mr-2 h-4 w-4" />,
      },
      {
        key: 'dates',
        label: 'Dates',
        icon: <Calendar className="mr-2 h-4 w-4" />,
      },
    ],
    [],
  )
  const actionHandlers = useMemo(
    () =>
      new Map([
        ['createtask', onCreateTask],
        ['undo', onUndo],
        ['redo', onRedo],
        ['toggleweathervisibility', onToggleWeatherVisibility],
        ['toggleweatherunit', onToggleWeatherUnit],
        ['toggletimevisibility', onToggleTimeVisibility],
        ['toggletimeformat', onToggleTimeFormat],
      ]),
    [
      onCreateTask,
      onRedo,
      onToggleTimeFormat,
      onToggleTimeVisibility,
      onToggleWeatherUnit,
      onToggleWeatherVisibility,
      onUndo,
    ],
  )
  const themeHandlers = useMemo(
    () =>
      new Map([
        ['light', () => setTheme('light')],
        ['dark', () => setTheme('dark')],
        ['system', () => setTheme('system')],
      ]),
    [setTheme],
  )
  const handleActionSelect = useCallback(
    (value) => {
      const handler = actionHandlers.get(value)
      if (handler) {
        runCommand(handler)
      }
    },
    [actionHandlers, runCommand],
  )
  const handleFeatureSelect = useCallback(
    (value) => {
      const key = value
      runCommand(() => actions.toggleTaskFeature(key))
    },
    [runCommand],
  )
  const handleThemeSelect = useCallback(
    (value) => {
      const handler = themeHandlers.get(value)
      if (handler) {
        runCommand(handler)
      }
    },
    [runCommand, themeHandlers],
  )
  const handleInputChange = useCallback(
    (value) => {
      setInputValue(value)
      if (feedback) {
        setFeedback('')
      }
    },
    [feedback],
  )
  const handleSmartAction = useCallback(async () => {
    const result = await runPrompt(inputValue)
    setFeedback(result.message)
    if (result.ok) {
      setOpen(false)
      setInputValue('')
    }
  }, [inputValue, runPrompt])
  const handleInputKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter' || !inputValue.trim()) return
      event.preventDefault()
      handleSmartAction()
    },
    [handleSmartAction, inputValue],
  )
  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder='Try "create a groceries list and add milk, eggs, bread"'
        value={inputValue}
        onValueChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
      />
      <CommandList className="min-h-[300px]">
        <CommandEmptyState feedback={feedback} />
        <CommandSmartGroup
          feedback={feedback}
          hasInput={Boolean(inputValue.trim())}
          isRunning={isRunning}
          onRun={handleSmartAction}
        />
        <CommandActionsGroup
          canUndo={canUndo}
          canRedo={canRedo}
          currentWeatherUnit={currentWeatherUnit}
          timeFormat={timeFormat}
          handleActionSelect={handleActionSelect}
        />
        <CommandSeparator />
        <CommandFeaturesGroup features={features} handleFeatureSelect={handleFeatureSelect} />
        <CommandSeparator />
        <CommandThemeGroup handleThemeSelect={handleThemeSelect} />
      </CommandList>
    </CommandDialog>
  )
}
const CommandEmptyState = ({ feedback }) => (
  <CommandEmpty>
    <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-muted/40">
        <SearchX className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{feedback || 'No results found'}</p>
        <p className="text-xs text-muted-foreground">
          {feedback
            ? 'Edit the prompt or try a more specific action.'
            : 'Try a different keyword or action.'}
        </p>
      </div>
    </div>
  </CommandEmpty>
)
const CommandSmartGroup = ({ feedback, hasInput, isRunning, onRun }) => {
  if (!hasInput && !feedback) return null

  return (
    <>
      <CommandGroup heading="Smart Actions">
        <CommandItem value="smart-action" onSelect={onRun}>
          <Sparkles className="mr-2 h-4 w-4" />
          <span>{isRunning ? 'Thinking...' : 'Run with Groq'}</span>
          <CommandShortcut className="text-[11px]">{isRunning ? '...' : 'Enter'}</CommandShortcut>
        </CommandItem>
      </CommandGroup>
      <CommandSeparator />
    </>
  )
}
const CommandActionsGroup = ({
  canUndo,
  canRedo,
  currentWeatherUnit,
  timeFormat,
  handleActionSelect,
}) => (
  <CommandGroup heading="Actions">
    <CommandItem value="createtask" onSelect={handleActionSelect}>
      <Plus className="mr-2 h-4 w-4" />
      <span>Create</span>
    </CommandItem>
    <CommandItem disabled={!canUndo} value="undo" onSelect={handleActionSelect}>
      <Undo className="mr-2 h-4 w-4" />
      <span>Undo</span>
      <CommandShortcutKeys keys={['⌘', 'Z']} />
    </CommandItem>
    <CommandItem disabled={!canRedo} value="redo" onSelect={handleActionSelect}>
      <Redo className="mr-2 h-4 w-4" />
      <span>Redo</span>
      <CommandShortcutKeys keys={['⇧', '⌘', 'Z']} />
    </CommandItem>
    <CommandItem value="toggleweathervisibility" onSelect={handleActionSelect}>
      <Thermometer className="mr-2 h-4 w-4" />
      <span>Weather</span>
    </CommandItem>
    <CommandItem value="toggleweatherunit" onSelect={handleActionSelect}>
      <Thermometer className="mr-2 h-4 w-4" />
      <span>{currentWeatherUnit === 'C' ? 'Fahrenheit' : 'Celsius'}</span>
    </CommandItem>
    <CommandItem value="toggletimevisibility" onSelect={handleActionSelect}>
      <Clock className="mr-2 h-4 w-4" />
      <span>Time</span>
    </CommandItem>
    <CommandItem value="toggletimeformat" onSelect={handleActionSelect}>
      <Clock className="mr-2 h-4 w-4" />
      <span>{timeFormat === '12' ? '24h' : '12h'}</span>
    </CommandItem>
  </CommandGroup>
)
const CommandShortcutKeys = ({ keys }) => (
  <CommandShortcut>
    <KbdGroup>
      {keys.map((key) => (
        <Kbd key={key}>{key}</Kbd>
      ))}
    </KbdGroup>
  </CommandShortcut>
)
const CommandFeaturesGroup = ({ features, handleFeatureSelect }) => {
  return (
    <CommandGroup heading="Feature Visibility">
      {features.map((feature) => (
        <CommandItem key={feature.key} value={feature.key} onSelect={handleFeatureSelect}>
          {feature.icon}
          <span>{feature.label}</span>
        </CommandItem>
      ))}
    </CommandGroup>
  )
}
const CommandThemeGroup = ({ handleThemeSelect }) => (
  <CommandGroup heading="Theme">
    <CommandItem value="light" onSelect={handleThemeSelect}>
      <Sun className="mr-2 h-4 w-4" />
      <span>Light</span>
    </CommandItem>
    <CommandItem value="dark" onSelect={handleThemeSelect}>
      <Moon className="mr-2 h-4 w-4" />
      <span>Dark</span>
    </CommandItem>
    <CommandItem value="system" onSelect={handleThemeSelect}>
      <Laptop className="mr-2 h-4 w-4" />
      <span>System</span>
    </CommandItem>
  </CommandGroup>
)
